// Glas-Tooltip (Handoff Tickets §8): EINE Ebene für einen Bereich. Elemente tragen `data-tip="Titel – Beschreibung"`;
// nach 0,4 s erscheint der Tooltip über bzw. unter dem Element (Hell/Dunkel über --tip-bg/--tip-ink).
// Kein natives `title`. Auf Geräten ohne Hover (Mobil/Touch) gibt es keine Tooltips.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { teileTooltip, tooltipPosition, type TooltipPosition } from '../../lib/ui/tooltipText';

const VERZOEGERUNG_MS = 420;

interface Sichtbar extends TooltipPosition { text: string }

/** Einmal je Bereich einhängen (z. B. in der Ticket-Seite).
 *  Die Pille (Dynamic Island) hat eine eigene Schicht (`nurPille`); Seiten-Schichten lassen ihre
 *  Elemente aus – sonst erschiene ein Tooltip in der Pille doppelt. */
export function GlasTooltipSchicht({ nurPille = false }: { nurPille?: boolean } = {}) {
  const [tip, setTip] = useState<Sichtbar | null>(null);
  const timer = useRef<number | null>(null);
  const aktuell = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const weg = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
      aktuell.current = null;
      setTip(null);
    };
    const zeige = (el: HTMLElement) => {
      const text = el.getAttribute('data-tip');
      // Mobil (< 900 px) keine Tooltips (Handoffs Tickets §8, Selbstauskunft §5)
      if (!text || window.innerWidth < 900) return;
      if (aktuell.current === el) return;
      weg();
      aktuell.current = el;
      timer.current = window.setTimeout(() => {
        if (!el.isConnected) return;
        const r = el.getBoundingClientRect();
        setTip({ text, ...tooltipPosition(r, window.innerWidth) });
      }, VERZOEGERUNG_MS);
    };
    // Zuständig nur für Elemente in der Pille (nurPille) bzw. nur außerhalb.
    const zustaendig = (el: HTMLElement) => !!el.closest('[data-pille]') === nurPille;
    const ueber = (e: MouseEvent) => {
      const roh = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tip]');
      const el = roh && zustaendig(roh) ? roh : null;
      if (el) zeige(el); else if (aktuell.current) weg();
    };
    const fokus = (e: FocusEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tip]');
      if (el && zustaendig(el) && (e.target as HTMLElement).matches(':focus-visible')) zeige(el); else weg();
    };
    document.addEventListener('mouseover', ueber);
    document.addEventListener('focusin', fokus);
    document.addEventListener('focusout', weg);
    document.addEventListener('mousedown', weg, true);
    window.addEventListener('scroll', weg, true);
    window.addEventListener('keydown', weg);
    return () => {
      weg();
      document.removeEventListener('mouseover', ueber);
      document.removeEventListener('focusin', fokus);
      document.removeEventListener('focusout', weg);
      document.removeEventListener('mousedown', weg, true);
      window.removeEventListener('scroll', weg, true);
      window.removeEventListener('keydown', weg);
    };
  }, [nurPille]);

  if (!tip) return null;
  const { titel, beschreibung } = teileTooltip(tip.text);
  const ende = tip.oben ? 'translate(-50%,-100%)' : 'translate(-50%,0)';
  const start = tip.oben ? 'translate(-50%,calc(-100% + 4px))' : 'translate(-50%,-4px)';
  return createPortal(
    <div aria-hidden className="immo-tip" style={{
      position: 'fixed', left: tip.x, top: tip.y, zIndex: 500, pointerEvents: 'none', maxWidth: 280,
      padding: '9px 12px', borderRadius: 14, background: 'var(--tip-bg)', color: 'var(--tip-ink)',
      backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      boxShadow: '0 12px 32px -10px rgba(0,0,0,0.45), inset 0 0 0 0.5px rgba(255,255,255,0.12)',
      fontFamily: "'Manrope', -apple-system, 'Helvetica Neue', sans-serif", lineHeight: 1.35,
      transform: ende, ['--tip-start' as string]: start, ['--tip-ende' as string]: ende,
      animation: 'immo-tip-in .16s ease-out',
    }}>
      <div style={{ fontSize: 12.5, fontWeight: 800 }}>{titel}</div>
      {beschreibung && <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.78, marginTop: 2 }}>{beschreibung}</div>}
    </div>,
    document.body,
  );
}
